import { useState, useEffect } from 'react';
import { Search, Users, UserCog, Plus, X, BookOpen } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

export function ClassAssignmentPage() {
  const { isAdmin } = useApp();
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [assigningClass, setAssigningClass] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [classData, teacherData] = await Promise.all([
        api.getClassAssignments(),
        api.listTeachers(),
      ]);
      setAssignments(classData);
      setTeachers(teacherData);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async (classSection) => {
    if (!selectedTeacher) return;
    try {
      await api.assignTeacherToClass(selectedTeacher, classSection, 'assign');
      toast.success('Teacher assigned');
      setAssigningClass(null);
      setSelectedTeacher('');
      loadData();
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };

  const handleUnassign = async (teacherId, classSection) => {
    try {
      await api.assignTeacherToClass(teacherId, classSection, 'unassign');
      toast.success('Teacher unassigned');
      loadData();
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };

  const filtered = assignments.filter(a =>
    a.classSection.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.teachers?.some(t => t.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div data-testid="class-assignment-page" className="space-y-6 animate-fade-in">
      <PageHeader title="Class Assignments" description="Assign teachers to classes and view class-teacher mapping." />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search by class or teacher name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="class-search" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(cs => (
            <DataCard key={cs.classSection}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-brand-deep-blue/10">
                    <BookOpen className="w-5 h-5 text-brand-deep-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Class {cs.classSection}</h3>
                    <p className="text-xs text-muted-foreground">{cs.studentCount} students</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Teachers</p>
                {cs.teachers?.length > 0 ? (
                  cs.teachers.map(t => (
                    <div key={t.userId} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                      <div>
                        <p className="text-sm font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.subject || 'No subject'}</p>
                      </div>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => handleUnassign(t.userId, cs.classSection)} data-testid={`unassign-${t.userId}-${cs.classSection}`}>
                          <X className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground py-2">No teachers assigned</p>
                )}
              </div>

              {isAdmin && (
                <div>
                  {assigningClass === cs.classSection ? (
                    <div className="flex gap-2">
                      <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                        <SelectTrigger className="flex-1" data-testid={`select-teacher-${cs.classSection}`}>
                          <SelectValue placeholder="Select teacher" />
                        </SelectTrigger>
                        <SelectContent>
                          {teachers.filter(t => !cs.teachers?.some(ct => ct.userId === t.userId)).map(t => (
                            <SelectItem key={t.userId} value={t.userId}>{t.name} ({t.subject || '-'})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => handleAssign(cs.classSection)} data-testid={`confirm-assign-${cs.classSection}`}>
                        <Plus className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setAssigningClass(null); setSelectedTeacher(''); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" className="w-full" onClick={() => setAssigningClass(cs.classSection)} data-testid={`assign-btn-${cs.classSection}`}>
                      <Plus className="w-4 h-4 mr-1" /> Assign Teacher
                    </Button>
                  )}
                </div>
              )}
            </DataCard>
          ))}
        </div>
      )}
    </div>
  );
}
